import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import io from 'socket.io-client';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, getCurrentTenant } = useAuth();
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    basic: 0,
    critical: 0,
    byEntity: {}
  });
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [salesBuddyNotifications, setSalesBuddyNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [newItems, setNewItems] = useState({
    total: 0,
    byEntity: {}
  });

  // Get current tenant code
  const getTenantCode = useCallback(() => {
    const currentTenant = getCurrentTenant();
    return currentTenant?.code || currentTenant?.tenant_code;
  }, [getCurrentTenant]);

  // Check if authentication is ready
  const isAuthReady = useCallback(() => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    // Fix: userId can be 0 for admin users, so check for user existence and either userId or adminId
    const hasUser = !!(user && (user.userId !== undefined || user.adminId !== undefined));
    const hasAxiosAuth = !!axios.defaults.headers.common['Authorization'];
    const hasBaseURL = !!axios.defaults.baseURL;
    
    console.log('🔔 Auth readiness check:', {
      hasToken,
      hasUser,
      hasAxiosAuth,
      hasBaseURL,
      token: token ? 'EXISTS' : 'MISSING',
      userId: user?.userId ?? 'NONE',
      adminId: user?.adminId ?? 'NONE'
    });
    
    return hasToken && hasUser && hasAxiosAuth && hasBaseURL;
  }, [user]);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (isAuthReady()) {
      console.log('🔔 Initializing notification socket...', {
        user: user.userId,
        apiUrl: process.env.REACT_APP_API_URL,
        defaultUrl: window.location.origin
      });

      // Use REACT_APP_API_URL if set, otherwise use current domain (window.location.origin)
      const socketUrl = process.env.REACT_APP_API_URL || window.location.origin;
      console.log('🔔 Connecting to Socket.IO:', socketUrl);
      
      const socketInstance = io(socketUrl, {
        auth: {
          token: localStorage.getItem('token')
        },
        transports: ['websocket', 'polling'],
        forceNew: true,
        timeout: 20000
      });

      socketInstance.on('connect', () => {
        console.log('🔔 Notification socket connected', socketInstance.id);
        setConnected(true);
        
        // Join user room for notifications
        socketInstance.emit('join', { userId: user.userId });
        console.log('🔔 Joined notification room for user:', user.userId);
      });

      socketInstance.on('connect_error', (error) => {
        console.error('🔔 Socket connection error:', error);
        setConnected(false);
      });

      socketInstance.on('disconnect', (reason) => {
        console.log('🔔 Notification socket disconnected:', reason);
        setConnected(false);
      });

      // Listen for new notifications
      socketInstance.on('notification', (notification) => {
        console.log('🔔 New notification received:', notification);
        
        // Add to notifications list
        setNotifications(prev => [notification, ...prev]);
        
        // Handle critical notifications
        if (notification.notificationLevel === 'Critical') {
          setCriticalAlerts(prev => [...prev, {
            id: notification.notificationId,
            title: notification.title,
            message: notification.message,
            entityType: notification.entityType,
            entityId: notification.entityId,
            createdAt: notification.createdAt,
            autoHide: false // Critical alerts don't auto-hide
          }]);
        }
      });

      // Listen for SalesBuddy channel notifications
      socketInstance.on('salesbuddyNotification', (notification) => {
        console.log('🤖 SalesBuddy notification received:', notification);
        setSalesBuddyNotifications(prev => [...prev, notification]);
      });

      // Listen for notification count updates
      socketInstance.on('notificationCount', (newCounts) => {
        console.log('🔔 Notification counts updated:', newCounts);
        setCounts(newCounts);
      });

      setSocket(socketInstance);

      return () => {
        socketInstance.disconnect();
      };
    }
  }, [user, isAuthReady]);

  const fetchNotificationCounts = useCallback(async () => {
    try {
      if (!isAuthReady()) {
        console.log('🔔 Skipping notification counts fetch - authentication not ready');
        return;
      }
      
      console.log('🔔 Fetching notification counts...');
      const tenantCode = getTenantCode();
      console.log('🔔 Current user:', user);
      console.log('🔔 Current tenant:', tenantCode);
      console.log('🔔 Current token:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');
      console.log('🔔 Axios base URL:', axios.defaults.baseURL);
      console.log('🔔 Axios auth header:', axios.defaults.headers.common['Authorization'] ? 'SET' : 'NOT SET');
      
      const response = await axios.get('/api/notifications/count', {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      console.log('🔔 Notification counts response:', response.data);
      console.log('🔔 Response status:', response.status);
      
      if (response.data.success) {
        console.log('🔔 Setting counts:', response.data.data);
        setCounts(response.data.data);
      } else {
        console.error('🔔 API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('🔔 Error fetching notification counts:', error);
      if (error.message === 'Access token required') {
        console.log('🔔 Authentication not ready yet, will retry later');
        return;
      }
      console.error('🔔 Error details:', error.response?.data);
    }
  }, [user, getTenantCode, isAuthReady]);

  const fetchNotifications = useCallback(async (options = {}) => {
    if (!isAuthReady()) {
      console.log('🔔 Skipping notifications fetch - authentication not ready');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const tenantCode = getTenantCode();
      const params = new URLSearchParams({
        page: options.page || 1,
        limit: options.limit || 50,
        ...(options.level && { level: options.level }),
        ...(options.read !== undefined && { read: options.read })
      });

      const response = await axios.get(`/api/notifications?${params}`, {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      if (response.data.success) {
        const newNotifications = response.data.data.notifications;
        if (options.page && options.page > 1) {
          // Append for pagination
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          // Replace for initial load
          setNotifications(newNotifications);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (error.message === 'Access token required') {
        console.log('🔔 Authentication not ready yet, will retry later');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [user, getTenantCode, isAuthReady]);

  const fetchCriticalNotifications = useCallback(async () => {
    try {
      if (!isAuthReady()) {
        console.log('🔔 Skipping critical notifications fetch - authentication not ready');
        return;
      }
      
      const tenantCode = getTenantCode();
      const response = await axios.get('/api/notifications/critical', {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      if (response.data.success) {
        const criticalNotifications = response.data.data.map(notification => ({
          id: notification.NotificationID,
          title: notification.Title,
          message: notification.Message,
          entityType: notification.EntityType,
          entityId: notification.EntityID,
          createdAt: notification.CreatedAt,
          autoHide: false
        }));
        setCriticalAlerts(criticalNotifications);
      }
    } catch (error) {
      console.error('Error fetching critical notifications:', error);
      if (error.message === 'Access token required') {
        console.log('🔔 Authentication not ready yet, will retry later');
        return;
      }
    }
  }, [user, getTenantCode, isAuthReady]);

  const fetchNewItems = useCallback(async () => {
    try {
      if (!isAuthReady()) {
        console.log('🆕 Skipping new items fetch - authentication not ready');
        return;
      }
      
      console.log('🆕 Fetching recently created items...');
      const tenantCode = getTenantCode();
      const response = await axios.get('/api/notifications/recent-items', {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      console.log('🆕 New items response:', response.data);
      
      if (response.data.success) {
        console.log('🆕 Setting new items:', response.data.data);
        setNewItems(response.data.data);
      } else {
        console.error('🆕 API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('🆕 Error fetching new items:', error);
      if (error.message === 'Access token required') {
        console.log('🆕 Authentication not ready yet, will retry later');
        return;
      }
      console.error('🆕 Error details:', error.response?.data);
    }
  }, [user, getTenantCode, isAuthReady]);

  // Fetch initial notification data with retry mechanism
  useEffect(() => {
    if (isAuthReady()) {
      console.log('🔔 Authentication ready, fetching notification data...', user.userId);
      
      const fetchWithRetry = async (retryCount = 0) => {
        try {
          await fetchNotificationCounts();
          await fetchNotifications();
          await fetchNewItems();
        } catch (error) {
          if (error.message === 'Access token required' && retryCount < 3) {
            console.log(`🔔 Authentication not ready, retrying in ${(retryCount + 1) * 1000}ms... (attempt ${retryCount + 1}/3)`);
            setTimeout(() => fetchWithRetry(retryCount + 1), (retryCount + 1) * 1000);
          }
        }
      };
      
      // Add a delay to ensure authentication is fully set up and axios headers are configured
      setTimeout(() => {
        fetchWithRetry();
      }, 1000);
    } else {
      console.log('🔔 Skipping notification fetch - authentication not ready');
    }
  }, [user, isAuthReady]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const tenantCode = getTenantCode();
      const response = await axios.put(`/api/notifications/${notificationId}/read`, {}, {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      if (response.data.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notification => 
            notification.NotificationID === notificationId 
              ? { ...notification, IsRead: true, ReadAt: new Date().toISOString() }
              : notification
          )
        );
        
        // Refresh counts (the backend will emit updated counts via socket too)
        await fetchNotificationCounts();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, [fetchNotificationCounts, getTenantCode]);

  // COMMENTED OUT: Mark all notifications as read (not currently used, backend endpoint disabled)
  // Backend route was causing slow requests (500-1400ms) and is not actively used
  // Commented out on 2026-01-29 to improve performance
  const markAllAsRead = useCallback(async (entityType = null) => {
    // Endpoint disabled - just log and return
    console.log(`🔔 markAllAsRead called but endpoint is disabled (${entityType || 'all'})`);
    return;
    
    /* ORIGINAL CODE - DISABLED
    try {
      console.log(`🔔 Marking all ${entityType || 'all'} notifications as read...`);
      const tenantCode = getTenantCode();
      const response = await axios.put('/api/notifications/read-all', {
        ...(entityType && { entityType })
      }, {
        headers: {
          'X-Tenant-Code': tenantCode
        }
      });
      
      console.log('🔔 Mark all as read response:', response.data);
      
      if (response && response.data && response.data.success) {
        console.log(`🔔 Successfully marked ${entityType || 'all'} notifications as read`);
        // Update local state
        setNotifications(prev => 
          prev.map(notification => 
            (!entityType || notification.EntityType === entityType)
              ? { ...notification, IsRead: true, ReadAt: new Date().toISOString() }
              : notification
          )
        );
        
        // Refresh counts
        await fetchNotificationCounts();
      } else {
        console.warn('🔔 API returned success=false or invalid response:', response?.data);
      }
    } catch (error) {
      console.error('🔔 Error marking all notifications as read:', error);
      console.error('🔔 Error response:', error.response?.data);
      console.error('🔔 Error status:', error.response?.status);
      // Don't throw the error - just log it so the navigation still works
    }
    */
  }, [getTenantCode]);

  const dismissCriticalAlert = useCallback((alertId) => {
    setCriticalAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  const dismissAllCriticalAlerts = useCallback(() => {
    setCriticalAlerts([]);
  }, []);

  const clearSalesBuddyNotifications = useCallback(() => {
    setSalesBuddyNotifications([]);
  }, []);

  // Get count for specific entity type
  const getEntityCount = useCallback((entityType) => {
    return counts.byEntity[entityType.toLowerCase()] || 0;
  }, [counts]);

  // Get count for specific notification level
  const getLevelCount = useCallback((level) => {
    return counts[level.toLowerCase()] || 0;
  }, [counts]);

  // Get count for recently created items by entity type
  const getNewItemCount = useCallback((entityType) => {
    if (!entityType) {
      return newItems.total || 0;
    }
    return newItems.byEntity[entityType.toLowerCase()] || 0;
  }, [newItems]);

  const value = {
    // State
    notifications,
    counts,
    criticalAlerts,
    salesBuddyNotifications,
    loading,
    connected,
    socket,
    newItems,

    // Actions
    fetchNotifications,
    fetchNotificationCounts,
    fetchCriticalNotifications,
    fetchNewItems,
    markAsRead,
    markAllAsRead,
    dismissCriticalAlert,
    dismissAllCriticalAlerts,
    clearSalesBuddyNotifications,

    // Helpers
    getEntityCount,
    getLevelCount,
    getNewItemCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};